import { useCallback, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { ExhPatientData, PatientData } from "types/patient";

import { convertExhToPatientData } from "../utils/patientConverters";

interface UseExhPatientResult {
  patient: PatientData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single patient by ID from ExtraHorizon
 * Uses the patient schema which is intercepted and redirected to patient-endpoint
 */
export const useExhPatient = (patientId: string | undefined): UseExhPatientResult => {
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  const loadPatient = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      setPatient(null);
      return;
    }

    if (!isExhClientInitialized()) {
      setLoading(false);
      setError(new Error("ExtraHorizon client not initialized"));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const client = getExhClient();

      // Fetch the patient document by ID using findFirst
      // This will be intercepted and redirected to patient-endpoint
      const rql = rqlBuilder().eq("id", patientId).build();
      const exhPatient = (await client.data.documents.findFirst("patient", {
        rql,
      })) as ExhPatientData | null;

      if (!exhPatient) {
        const notFoundError = new Error("Patient not found");
        setError(notFoundError);
        setPatient(null);
        return;
      }

      // Convert ExH format to PatientData format
      const patientData = convertExhToPatientData(exhPatient);
      setPatient(patientData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch patient");
      setError(error);
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [patientId, onError]);

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  return { patient, loading, error, refetch: loadPatient };
};
